import {
  Badge,
  Box,
  Color,
  FontFamily,
  Icon,
  VisuallyHidden,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CSSObject } from '@emotion/react'
import React from 'react'

import { CWCPriceComparisonBrand, CWCPriceComparisonDimension } from '../../config'

// Responsive bands map to [base(<640), 640, 768 (iPad), 1024 (desktop)] — the CwC 4-value convention
// (a 3-value array lumps every width >=768 onto the tablet value). Per Figma the table uses the
// DESKTOP token scale from iPad up (note 629:1497), so iPad (index 2) shares desktop (index 3) table
// values. Numeric px values are Figma-current; the figma-fidelity-loop reconciles them against the
// prototype (the Figma is a lossy html.to.design capture).

// Comparison table — a real <table> (designer note 482:1168: "add table as code for SEO"). Each brand
// is its OWN rounded card (green Gousto + two white competitor cards) floating on the grey ColdGrey_50
// surface, with grey gaps showing between columns. `border-collapse: separate` + `border-spacing`
// creates the horizontal gaps (vertical 0 so each column's cells stack flush into one continuous
// card). The Gousto card is taller — its first thead row is a green "pill band"; the white competitor
// cards begin one row lower (at the brand-name row), so grey shows above the competitor names.
const Table = withCitrusTheme('table', (theme) => [
  {
    boxSizing: 'border-box',
    borderCollapse: 'separate',
    borderSpacing: '8px 0',
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderRadius: '16px',
    tableLayout: 'fixed',
    width: '100%',
    maxWidth: '535px',
    margin: '0 auto',
    '& col:nth-of-type(1)': { width: '26%' },
    '& col:nth-of-type(n+2)': { width: '22%' },
    // Gousto card outline (design review): a 1px green perimeter around the highlighted column. Left
    // + right on every Gousto cell, top on the pill-band cell, bottom on the last body row — with
    // vertical border-spacing 0 the cells stack flush into a continuous rounded-rect outline. Scoped
    // to td/th so it doesn't hit the Gousto value <span>s (which also carry data-gousto).
    '& td[data-gousto="true"], & th[data-gousto="true"]': {
      borderLeft: '1px solid rgba(14, 124, 67, 0.38)',
      borderRight: '1px solid rgba(14, 124, 67, 0.38)',
    },
    // Gousto card top corners + top border (the green pill-band cell in the first thead row).
    '& thead tr:first-of-type td[data-gousto="true"]': {
      borderTop: '1px solid rgba(14, 124, 67, 0.38)',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
    },
    // Competitor card top corners (the brand-name row, non-Gousto headers).
    '& thead tr:last-of-type th:not([data-gousto="true"])': {
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
    },
    // All brand cards' bottom corners (row-label cells are <th>, so excluded).
    '& tbody tr:last-of-type td': {
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
    },
    // Gousto card bottom border (completes the outline on the last body row).
    '& tbody tr:last-of-type td[data-gousto="true"]': {
      borderBottom: '1px solid rgba(14, 124, 67, 0.38)',
    },
    // No separator between the brand headers and the first data row (Figma borders only between rows).
    '& tbody tr:first-of-type > *': { borderTop: 'none' },
    // Tablet + desktop share one identical table (design review): 14px column gaps from ≥768 up.
    [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: { borderSpacing: '14px 0' },
  } as CSSObject,
  // Horizontal padding trimmed by ~the gap so the outer card edges stay tight (border-spacing adds a
  // gap at the outer edges too).
  // Mobile: 0/6 padding + the 8px border-spacing lands the row-label text 14px from the left card
  // edge and the Simmer card 14px from the right (design-review directive).
  theme.responsiveStyle(['0px', '0px', '16px', '16px'], ['paddingLeft']) as CSSObject,
  theme.responsiveStyle(['6px', '6px', '16px', '16px'], ['paddingRight']) as CSSObject,
  theme.responsiveStyle(['20px', '20px', '24px', '24px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '28px', '28px'], ['paddingBottom']) as CSSObject,
])

// Gousto "pill band" — the green top extension of the Gousto card that holds the "Best value" pill,
// inset from the card's top edge. Competitor columns have transparent spacer cells in this row, so
// the grey surface shows above their names and the Gousto card reads as taller.
const PillBandCell = withCitrusTheme('td', (theme) => ({
  backgroundColor: theme.color(Color.Success_50),
  textAlign: 'center',
  verticalAlign: 'bottom',
  paddingTop: '14px',
  paddingBottom: '4px',
}))

// Visually-hidden caption — names the table for screen readers without altering the visual design.
const HiddenCaption = withCitrusTheme('caption', () => ({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}))

// Brand-column header. Competitor columns are White cards; the Gousto column is the pale-green
// highlight (Success_50) with a brand-red header. Header uses Primary_900 (#D30016 — the darkest
// brand red) not Primary_600: on the Success_50 cell Primary_600 is only 3.55:1 (AA fail for this
// 12–16px text), whereas Primary_900 clears 4.5:1 while staying on-brand. Corner rounding is applied
// at the Table level.
// Brand-header type ramps 12px (mobile) → 16px (iPad/desktop) per ui-design so the names never crowd
// the narrow mobile columns.
const HeaderCell = withCitrusTheme('th', (theme) => [
  {
    textAlign: 'center',
    verticalAlign: 'bottom',
    padding: '8px 6px 12px',
    fontFamily: theme.tokens.FontFamilies.bold,
    backgroundColor: theme.color(Color.White),
    color: theme.color(Color.ColdGrey_800),
    '&[data-gousto="true"]': {
      backgroundColor: theme.color(Color.Success_50),
      color: theme.color(Color.Primary_900),
    },
  } as CSSObject,
  theme.responsiveStyle(['12px', '12px', '16px', '16px'], ['fontSize']) as CSSObject,
])

// Row-label column (stays on the grey card surface, not a white card). Bold per Figma. No separator
// lines — in Figma the row hairlines live inside the brand cards only, not across the label column.
const RowHeaderCell = withCitrusTheme('th', (theme) => [
  {
    textAlign: 'left',
    verticalAlign: 'middle',
    padding: '12px 6px',
    fontFamily: theme.tokens.FontFamilies.bold,
    color: theme.color(Color.ColdGrey_800),
  } as CSSObject,
  // 16/24 from the Citrus FontSize scale (size 4) rather than a raw px pair.
  theme.responsiveFont(4) as CSSObject,
])

// Brand data cell. Competitor cells are White; Gousto is Success_50 with a green-tinted separator.
const DataCell = withCitrusTheme('td', (theme) => ({
  textAlign: 'center',
  verticalAlign: 'middle',
  padding: '12px 6px',
  backgroundColor: theme.color(Color.White),
  color: theme.color(Color.ColdGrey_800),
  borderTop: `1px solid rgba(26, 32, 38, 0.08)`,
  '&[data-gousto="true"]': {
    backgroundColor: theme.color(Color.Success_50),
    borderTop: `1px solid rgba(14, 124, 67, 0.16)`,
  },
}))

// "from" caption above the price — small. ColdGrey_600 (not _400): this 12px text qualifies the
// claim (starting price, not absolute), so it must clear AA; _400 is ~3.8:1 on white / ~3.4:1 on the
// Gousto cell, _600 clears 4.5:1 on both.
const FromCaption = withCitrusTheme('span', (theme) => ({
  display: 'block',
  fontSize: '12px',
  color: theme.color(Color.ColdGrey_600),
}))

// Price / weekly-recipes value. Bold (per Figma), Gousto emphasised in Success_900; competitors in
// ColdGrey_800. Gousto price and weekly-recipes are the same size (18px mobile → 28px from tablet), so
// there is a single Gousto size (no price/recipes split). Competitors 16px → 24px.
const ValueText = withCitrusTheme('span', (theme) => [
  {
    display: 'block',
    fontFamily: theme.tokens.FontFamilies.bold,
    letterSpacing: '-0.02em',
    '&[data-gousto="true"]': { color: theme.color(Color.Success_900) },
  } as CSSObject,
  theme.responsiveStyle(['16px', '16px', '24px', '24px'], ['fontSize']) as CSSObject,
  {
    '&[data-gousto="true"]': theme.responsiveStyle(
      ['18px', '18px', '28px', '28px'],
      ['fontSize'],
    ) as CSSObject,
  } as CSSObject,
])

// "Best value" pill — Badge has no bg/color prop, so override via theme: Success_900 bg + white text.
// CRITICAL: Badge renders its label through an inner Text `<span>` that sets its OWN default
// font-size (16px) + weight, which out-specifies a font-size on the Badge root — so the size/tracking
// MUST target `& span` or the pill renders oversized (~94px→overflows the column). Compact padding
// too (Badge's default is too large). Sizes: 8px/0.24px tracking base → 10.5px/0.84px from iPad up.
const BestValuePill = withCitrusTheme(Badge, (theme) => [
  {
    backgroundColor: theme.color(Color.Success_900),
    color: theme.color(Color.White),
    whiteSpace: 'nowrap',
    '& span': {
      fontFamily: theme.tokens.FontFamilies[FontFamily.ExtraBold],
      lineHeight: 'normal',
      whiteSpace: 'nowrap',
      fontSize: '8px',
      letterSpacing: '0.24px',
      [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
        fontSize: '10.5px',
        letterSpacing: '0.84px',
      },
    },
  } as CSSObject,
  theme.responsiveStyle(['4px', '4px', '5px', '5px'], ['paddingTop', 'paddingBottom']) as CSSObject,
  theme.responsiveStyle(
    ['6px', '6px', '11px', '11px'],
    ['paddingLeft', 'paddingRight'],
  ) as CSSObject,
])

// Yes/No indicator disc: a single filled-disc Citrus glyph (success/clear) drawn in currentColor.
// Icon exposes no colour prop and its `size` is a SpaceScale multiplier (x4px) so 30/38px is
// off-scale — colour + size are set here. Yes = Success_900, No = ColdGrey_500.
const iconDiscStyle = (theme: Parameters<Parameters<typeof withCitrusTheme>[1]>[0]) =>
  theme.responsiveStyle(['30px', '30px', '38px', '38px'], ['width', 'height']) as CSSObject
const YesIcon = withCitrusTheme(Icon, (theme) => [
  { color: theme.color(Color.Success_900) } as CSSObject,
  iconDiscStyle(theme),
])
const NoIcon = withCitrusTheme(Icon, (theme) => [
  { color: theme.color(Color.ColdGrey_500) } as CSSObject,
  iconDiscStyle(theme),
])

const YesNoCell = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  } as CSSObject,
  // 12px mobile, 14px from iPad up.
  theme.responsiveStyle(['12px', '12px', '14px', '14px'], ['fontSize']) as CSSObject,
])

function YesNoIndicator({ value }: { value: boolean }) {
  return (
    <YesNoCell>
      {value ? <YesIcon name="success" aria-hidden /> : <NoIcon name="clear" aria-hidden />}
      <span>{value ? 'Yes' : 'No'}</span>
    </YesNoCell>
  )
}

type CWCCompetitorsComparisonTableProps = {
  brands: CWCPriceComparisonBrand[]
  dimensions: CWCPriceComparisonDimension[]
}

// Presentational comparison table: Gousto vs its competitors across the configured dimensions. Pure —
// all copy/figures come in via `brands`/`dimensions`; the section shell owns layout, heading, CTA and
// tracking. Semantic <table> for SEO + screen readers (designer note 482:1168).
export function CWCCompetitorsComparisonTable({
  brands,
  dimensions,
}: CWCCompetitorsComparisonTableProps) {
  // Screen-reader caption, derived from `brands` so editing the config is the single update surface.
  const highlightedBrand = brands.find((brand) => brand.bestValue) ?? brands[0]
  const competitorNames = brands.filter((brand) => !brand.bestValue).map((brand) => brand.name)
  const tableCaption = `${highlightedBrand.name} compared with ${competitorNames.join(' and ')}`

  const renderCell = (brand: CWCPriceComparisonBrand, dimension: CWCPriceComparisonDimension) => {
    if (dimension.type === 'yesNo') {
      return <YesNoIndicator value={brand[dimension.key] as boolean} />
    }
    if (dimension.key === 'pricePerMeal') {
      return (
        <>
          <FromCaption>from</FromCaption>
          <ValueText data-gousto={brand.bestValue}>{brand.pricePerMeal}</ValueText>
        </>
      )
    }
    return <ValueText data-gousto={brand.bestValue}>{brand[dimension.key] as string}</ValueText>
  }

  return (
    <Table>
      <HiddenCaption>{tableCaption}</HiddenCaption>
      {/* Deterministic column widths for table-layout: fixed — widths defined on the Table's
          `col:nth-of-type` rules (row-label column wider; three brand columns share the rest). */}
      <colgroup>
        <col />
        <col />
        <col />
        <col />
      </colgroup>
      <thead>
        {/* Pill-band row: only the Gousto column has a (green) cell holding the "Best value"
            pill; the competitor columns are transparent spacers, so the grey surface shows above
            their names and the Gousto card reads as taller. */}
        <tr>
          <td aria-hidden />
          {brands.map((brand) =>
            brand.bestValue ? (
              <PillBandCell key={brand.name} data-gousto="true">
                <BestValuePill radius={999} uppercase>
                  {/* Name the brand for screen readers — the pill sits in a spacer row above
                      the brand-name header, so "Best value" alone has no column association. */}
                  <VisuallyHidden>{brand.name}: </VisuallyHidden>
                  Best value
                </BestValuePill>
              </PillBandCell>
            ) : (
              <td key={brand.name} aria-hidden />
            ),
          )}
        </tr>
        {/* Brand-name row */}
        <tr>
          <td aria-hidden />
          {brands.map((brand) => (
            <HeaderCell key={brand.name} scope="col" data-gousto={brand.bestValue}>
              {brand.name}
            </HeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {dimensions.map((dimension) => (
          <tr key={dimension.key}>
            <RowHeaderCell scope="row">{dimension.label}</RowHeaderCell>
            {brands.map((brand) => (
              <DataCell key={brand.name} data-gousto={brand.bestValue}>
                {renderCell(brand, dimension)}
              </DataCell>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  )
}
